import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, LifeBuoy, Radio, Send, X } from "lucide-react";
import { getSupportCase, listSupportCases, markSupportCaseRead, replyToSupportCase, type SupportCase } from "../api/support";
import { connectSocket } from "../api/socket";

export default function SupportInbox({ requestedCaseId, filter, search, onCountsChange }: {
  requestedCaseId?: string | null;
  filter: string;
  search: string;
  onCountsChange: (counts: { all: number; unread: number }) => void;
}) {
  const [cases, setCases] = useState<SupportCase[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totals, setTotals] = useState({ all: 0, unread: 0 });
  const [openCaseId, setOpenCaseId] = useState<string | null>(requestedCaseId || null);
  const [activeCase, setActiveCase] = useState<SupportCase | null>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const [listError, setListError] = useState("");
  const [sending, setSending] = useState(false);
  const streamRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const openCaseIdRef = useRef<string | null>(openCaseId);
  const pageRef = useRef(1);
  openCaseIdRef.current = openCaseId;

  const refresh = useCallback(async () => {
    try {
      const result = await listSupportCases(1);
      setCases((current) => pageRef.current > 1
        ? [...result.cases, ...current.filter((item) => !result.cases.some((latest) => latest.id === item.id))]
        : result.cases);
      setHasMore(result.hasMore);
      setTotals({ all: result.total, unread: result.unreadTotal });
      setListError("");
    } catch (failure) {
      setListError(failure instanceof Error ? failure.message : "Could not load support conversations.");
    }
  }, []);

  const loadMore = async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await listSupportCases(page + 1);
      setCases((current) => [...current, ...result.cases.filter((item) => !current.some((entry) => entry.id === item.id))]);
      setPage(page + 1);
      pageRef.current = page + 1;
      setHasMore(result.hasMore);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not load older conversations.");
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => { if (requestedCaseId) setOpenCaseId(requestedCaseId); }, [requestedCaseId]);
  useEffect(() => {
    const onFocus = () => { void refresh(); };
    const onVisible = () => { if (document.visibilityState === "visible") void refresh(); };
    const interval = window.setInterval(() => { if (document.visibilityState === "visible") void refresh(); }, 30000);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => { window.clearInterval(interval); window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onVisible); };
  }, [refresh]);

  const loadCase = useCallback(async (id: string) => {
    try {
      const result = await getSupportCase(id);
      if (openCaseIdRef.current !== id) return;
      setActiveCase(result.supportCase);
      setError("");
      if (result.supportCase.unreadCount > 0) {
        await markSupportCaseRead(id);
        if (openCaseIdRef.current === id) {
          setCases((current) => current.map((item) => item.id === id ? { ...item, unreadCount: 0 } : item));
          void refresh();
        }
      }
    } catch (failure) {
      if (openCaseIdRef.current === id) setError(failure instanceof Error ? failure.message : "Could not open this conversation.");
    }
  }, [refresh]);

  useEffect(() => {
    let socket: ReturnType<typeof connectSocket>;
    try { socket = connectSocket(); } catch { return; }
    const onUpdated = ({ caseId }: { caseId?: string }) => {
      void refresh();
      if (caseId && caseId === openCaseId) void loadCase(caseId);
    };
    socket.on("support:case:updated", onUpdated);
    return () => { socket.off("support:case:updated", onUpdated); };
  }, [loadCase, openCaseId, refresh]);

  useEffect(() => {
    if (!openCaseId) { setActiveCase(null); return; }
    setActiveCase(null);
    void loadCase(openCaseId);
    const interval = window.setInterval(() => { if (document.visibilityState === "visible") void loadCase(openCaseId); }, 15000);
    return () => window.clearInterval(interval);
  }, [openCaseId, loadCase]);

  const activeCaseId = activeCase?.id;
  const activeMessageCount = activeCase?.messages?.length ?? 0;
  useEffect(() => {
    if (!activeCaseId) return;
    streamRef.current?.scrollTo({ top: streamRef.current.scrollHeight, behavior: "smooth" });
  }, [activeCaseId, activeMessageCount]);

  useEffect(() => {
    if (!openCaseId) return;
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => dialogRef.current?.querySelector<HTMLButtonElement>("button")?.focus());
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenCaseId(null);
      if (event.key !== "Tab" || !dialogRef.current) return;
      const controls = Array.from(dialogRef.current.querySelectorAll<HTMLElement>("button:not([disabled]), textarea:not([disabled])"));
      if (!controls.length) return;
      if (event.shiftKey && document.activeElement === controls[0]) { event.preventDefault(); controls[controls.length - 1].focus(); }
      if (!event.shiftKey && document.activeElement === controls[controls.length - 1]) { event.preventDefault(); controls[0].focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => { window.cancelAnimationFrame(frame); document.body.style.overflow = previousOverflow; window.removeEventListener("keydown", onKey); if (previousFocus?.isConnected) previousFocus.focus(); };
  }, [openCaseId]);

  const searchable = cases.filter((item) => {
    const query = search.trim().toLowerCase();
    return !query || "blackspace support".includes(query) || item.category.toLowerCase().includes(query) || item.lastPreview.toLowerCase().includes(query);
  });
  const visible = searchable.filter((item) => filter === "all" || (filter === "unread" && item.unreadCount > 0));
  const unreadCount = searchable.filter((item) => item.unreadCount > 0).length;
  useEffect(() => { onCountsChange(search.trim() ? { all: searchable.length, unread: unreadCount } : totals); }, [search, searchable.length, unreadCount, totals, onCountsChange]);

  const sendReply = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!openCaseId || draft.trim().length < 2 || sending) return;
    setSending(true);
    setError("");
    try {
      const result = await replyToSupportCase(openCaseId, draft.trim());
      setActiveCase(result.supportCase);
      setDraft("");
      await refresh();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not send your reply.");
    } finally {
      setSending(false);
    }
  };

  const thread = openCaseId && (
    <div className="fixed inset-0 z-[2450] flex items-center justify-center bg-[#010207]/90 text-white backdrop-blur-xl sm:p-5" role="presentation">
      <section ref={dialogRef} role="dialog" aria-modal="true" aria-label="Blackspace Support conversation" className="flex h-[100dvh] w-full max-w-3xl flex-col overflow-hidden border border-cyan-300/15 bg-[#060912] shadow-[0_25px_100px_rgba(0,0,0,.75)] sm:h-[min(88dvh,840px)] sm:rounded-2xl">
        <header className="flex shrink-0 items-center gap-3 border-b border-white/[.07] bg-[radial-gradient(circle_at_10%_0%,rgba(34,211,238,.1),transparent_55%)] px-4 py-4">
          <button type="button" onClick={() => setOpenCaseId(null)} aria-label="Back to inbox" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-white/10 text-slate-300 hover:bg-white/[.07]"><ArrowLeft size={18} /></button>
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-cyan-300/20 bg-cyan-300/[.07] text-cyan-200"><LifeBuoy size={20} /></span>
          <div className="min-w-0 flex-1"><h2 className="truncate text-sm font-bold">Blackspace Support</h2><p className="mt-0.5 text-xs text-slate-400">{activeCase ? `${activeCase.category} · ${activeCase.status}` : "Loading conversation…"}</p></div>
          <button type="button" onClick={() => setOpenCaseId(null)} aria-label="Close conversation" className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-white/[.07] hover:text-white"><X size={17} /></button>
        </header>
        <div ref={streamRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-[radial-gradient(circle_at_50%_20%,rgba(82,41,136,.07),transparent_60%)] px-4 py-6 sm:px-7" role="log" aria-label="Support messages">
          {activeCase?.messages?.map((entry) => <div key={entry.id} className={`flex ${entry.author === "me" ? "justify-end" : "justify-start"}`}><div className={`max-w-[min(85%,34rem)] rounded-2xl border px-4 py-3 ${entry.author === "me" ? "border-violet-300/20 bg-violet-300/10 text-violet-50" : "border-cyan-300/20 bg-cyan-300/[.06] text-slate-100"}`}><p className="mb-1 text-[10px] font-bold text-slate-400">{entry.author === "me" ? "You" : "Blackspace Support"}</p><p className="whitespace-pre-wrap break-words text-sm leading-6">{entry.body}</p><time className="mt-2 block text-right text-[10px] text-slate-500">{new Date(entry.createdAt).toLocaleString()}</time></div></div>)}
          {!activeCase && !error && <p className="text-center text-sm text-slate-500">Loading…</p>}
        </div>
        {error && <p role="alert" className="mx-4 rounded-lg border border-rose-300/20 bg-rose-300/[.07] px-3 py-2 text-xs text-rose-200">{error}</p>}
        <form onSubmit={sendReply} className="flex shrink-0 items-end gap-2 border-t border-white/[.07] bg-[#070a12] px-4 py-4 sm:px-7">
          <label className="min-w-0 flex-1"><span className="sr-only">Reply to Blackspace Support</span><textarea value={draft} onChange={(event) => { setDraft(event.target.value.slice(0, 3000)); setError(""); }} disabled={!activeCase || activeCase.status === "closed" || sending} rows={2} maxLength={3000} className="max-h-32 min-h-11 w-full resize-y rounded-xl border border-white/10 bg-black/35 px-3 py-2 text-sm text-white outline-none focus:border-cyan-300/40 disabled:opacity-50" /></label>
          <button type="submit" disabled={!activeCase || activeCase.status === "closed" || draft.trim().length < 2 || sending} aria-label="Send support reply" className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-cyan-300/30 bg-cyan-300/10 text-cyan-100 hover:bg-cyan-300/20 disabled:opacity-40"><Send size={17} /></button>
        </form>
        {activeCase?.status === "closed" && <p className="px-4 pb-3 text-xs text-slate-500 sm:px-7">This conversation is closed. You can start a new support request from the menu.</p>}
      </section>
    </div>
  );

  return <>
    {visible.map((item) => <button key={item.id} type="button" onClick={() => setOpenCaseId(item.id)} className="flex w-full items-center gap-3 border-b border-white/[.05] px-4 py-3 text-left transition hover:bg-cyan-300/[.04]"><span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-cyan-300/20 bg-cyan-300/[.06] text-cyan-200"><Radio size={18} /></span><span className="min-w-0 flex-1"><span className="flex items-center gap-2"><strong className="truncate text-sm text-white">Blackspace Support</strong><span className="truncate text-[10px] text-slate-500">{item.category}</span></span><span className="mt-1 block truncate text-xs text-slate-400">{item.lastPreview}</span></span>{item.unreadCount > 0 && <span className="grid min-h-5 min-w-5 shrink-0 place-items-center rounded-full bg-cyan-300 px-1 text-[10px] font-bold text-black">{item.unreadCount}</span>}</button>)}
    {listError && <button type="button" onClick={() => void refresh()} className="w-full px-4 py-2 text-left text-xs text-rose-200" role="alert">{listError} · Tap to retry</button>}
    {!openCaseId && error && <p role="alert" className="px-4 py-2 text-xs text-rose-200">{error}</p>}
    {(filter === "all" || filter === "unread") && hasMore && <button type="button" onClick={() => void loadMore()} disabled={loadingMore} className="w-full px-4 py-3 text-center text-xs font-semibold text-cyan-200 hover:bg-cyan-300/[.04] disabled:opacity-50">{loadingMore ? "Loading…" : "Load older support conversations"}</button>}
    {typeof document !== "undefined" && thread ? createPortal(thread, document.body) : null}
  </>;
}
